import React from 'react';
import { Cell, DateTimeField, TextLink } from 'ui-builder';
import { WebhookRequestLogEntry, useWebhookMonitoringService, useDateHelper } from 'nekst-api';
import { ShowMoreTextField } from '../../../shared/widgets/ShowMoreTextWrapper';
import { useShowContextRequired } from 'ui-builder';
import LoadMorePaginationList from '../../../shared/uibuilder/list/LoadMorePaginationList';
import { useSubscriptionEventHelper } from '../subscriptionEventHelper';
import InfiniteScrollWrapper from '../../../shared/uibuilder/list/layout/InfiniteScrollWrapper';
import { GridLayout } from 'features/nekst-widgets';

interface WebhookRequestsLogEntriesListProps {
  subscriptionId: number;
}

function formatRequestData(data: WebhookRequestLogEntry): string {
  const parts = [];

  parts.push('Request URL:\n' + data.url);
  parts.push('\nRequest Payload:\n' + data.payload);

  if (data.serverResponse) {
    if (data.serverResponse.headers) {
      parts.push('\nResponse Headers:\n' + Object.entries(data.serverResponse.headers)
        .map(([key, values]) => `${key}: ${values.join(', ')}`)
        .join('\n')
      );
    }

    if (data.serverResponse.body) {
      parts.push('\nResponse Body:\n' + data.serverResponse.body);
    }
  } else {
    parts.push('\nNo Response Data');
  }

  return parts.join('\n');
}

function DownloadRequestLink() {
  const showContext = useShowContextRequired<WebhookRequestLogEntry>();

  const dateHelper = useDateHelper();

  const handleDownload = () => {
    const content = formatRequestData(showContext.data!);

    // Create blob and download
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `webhook-request-${dateHelper.toString(showContext.data!.when)}.txt`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <TextLink
      onClick={handleDownload}
    >
      Download Response
    </TextLink>
  );
}

function EventType() {
  const { data } = useShowContextRequired<WebhookRequestLogEntry>();

  const payload = data!.payload;
  const helper = useSubscriptionEventHelper();

  return (
    <>
      {helper.getLabel(payload.event)}
      {' '}
      (id: {payload.id})
    </>
  );
}

export default function WebhookRequestsLogEntriesList(props: WebhookRequestsLogEntriesListProps) {
  const webhookMonitoringService = useWebhookMonitoringService();

  return (
    <LoadMorePaginationList
      getDataFunc={async (filter, nextPageToken
      ) => {
        return await webhookMonitoringService.getHistory({
          subscriptionId: props.subscriptionId,
          limit: 20,
          fromTimestamp: nextPageToken as number | undefined,
        });
      }}
    >
      <InfiniteScrollWrapper>
        <GridLayout>
          <Cell label="When">
            <DateTimeField source="when" />
          </Cell>
          <Cell label="Event">
            <EventType />
          </Cell>
          <Cell label="Status">
            <ShowMoreTextField source="result" />
          </Cell>
          <Cell label="Actions">
            <DownloadRequestLink />
          </Cell>
        </GridLayout>
      </InfiniteScrollWrapper>

    </LoadMorePaginationList>
  );
}
